/**
 * @todo this models are based on API invariants, when making any changes
 * make sure to double check if it's in sync with the API mappings
 */
import { LOCATION_ID_KEY } from '@portal/common/models/data-key';
import { DataProjectId } from '@portal/common/models/data-project';
import { DataGranularityKey } from '@portal/common/types';

export const standardReportingParams = [
    'organization_id',
    'browser_name',
    'browser_language_code',
    'os_name',
    'device_type',
    'device_name',
    'country',
    'app_version',
    'is_new_account',
    'wildcarded_app_path',
    'source',

    // 'day_of_week',
    // 'hour',
    // 'event_id',
    // 'visit_id',
    // 'organization_id',
    // 'account_id',
    // 'days_since_registration',
    // 'organization_role',
    // 'app_path',
    // 'app_version_major',
    // 'app_version_minor',
    // 'app_version_patch',
    // 'country_code',
    // 'region',
    // 'city',
    // 'timezone',
    // 'os_version',
    // 'os_version_major',
    // 'os_version_minor',
    // 'browser_version',
    // 'browser_version_major',
    // 'browser_version_minor',
    // 'window_width',
    // 'window_height',
] as const;

export const pageViewReportingParams = [/*'prev_app_path',*/ 'wildcarded_prev_app_path'] as const;
export const fileDownloadReportingParams = [
    'resource_name',
    'resource_category_name', // 'resource_id',
    // 'resource_category_id',
    // 'download_time',
] as const;
export const conditionSearchReportingParams = [
    'search_term',
    'resource_id',
    'cause_id',
    'sequela_id',
    'rei_id',
    'cro_id',
    'pathogen_id',
    'risk_exposure_id',
    'covariate_id',
] as const;

export const userFeedbackReportingParams = ['reason', 'status', 'is_manager_copied'] as const;
export const dataExportStandardReportingParams = ['chart_types', 'format'] as const;
export const DELIVERY_METHOD_KEY = 'delivery_method';

export const pdfExportStandardReportingParams = [
    'chart_types',
    'pdf_report_options',
    DELIVERY_METHOD_KEY,
] as const;

export type ReportingParam =
    | (typeof standardReportingParams)[number]
    | (typeof pageViewReportingParams)[number]
    | (typeof fileDownloadReportingParams)[number]
    | (typeof conditionSearchReportingParams)[number]
    | (typeof userFeedbackReportingParams)[number]
    | (typeof dataExportStandardReportingParams)[number]
    | (typeof pdfExportStandardReportingParams)[number]
    | DataGranularityKey;

export enum AggregationType {
    uniqueCount = 'count_unique',
    count = 'count',
}

export enum ChartType {
    line = 'line',
    bar = 'bar',
    table = 'table',
    map = 'map',
}

export enum VisualizationType {
    TopLocations = 'top_locations',
    QueryVolume = 'query_volume',
    TotalVisits = 'total_visits',
    TotalQueries = 'total_queries',
    EngagementTrends = 'engagement_trends',
    LicenseInformation = 'license_information',
    QueriesByDataType = 'queries_by_data_type',
}

export const EXCLUDED_COMPANY_USAGE_DATA_PROJECTS = [DataProjectId.EDP];

export type VisualizationConfig = {
    type: VisualizationType;
    key: string;
    supportedDataProjectIds?: number[];
    filter?: DataGranularityKey;
    dataType?: string;
    disabled?: boolean;
    globalData?: boolean;
    withTimeUnitFilter?: boolean;
};

export const BASE_VISUALIZATION_OPTIONS: VisualizationConfig[] = [
    {
        type: VisualizationType.TopLocations,
        key: VisualizationType.TopLocations,
    },
    {
        type: VisualizationType.QueryVolume,
        filter: LOCATION_ID_KEY,
        key: `${VisualizationType.QueryVolume}_${LOCATION_ID_KEY}`,
    },
    {
        type: VisualizationType.TotalVisits,
        key: VisualizationType.TotalVisits,
        globalData: true,
        withTimeUnitFilter: true,
    },
    {
        type: VisualizationType.TotalQueries,
        key: VisualizationType.TotalQueries,
        globalData: true,
        withTimeUnitFilter: true,
    },
    {
        type: VisualizationType.EngagementTrends,
        key: VisualizationType.EngagementTrends,
        globalData: true,
        withTimeUnitFilter: true,
    },
    {
        type: VisualizationType.LicenseInformation,
        key: VisualizationType.LicenseInformation,
        globalData: true,
    },
    {
        type: VisualizationType.QueriesByDataType,
        key: VisualizationType.QueriesByDataType,
    },
];

export enum EventType {
    visit = 'visit',
    pageView = 'pageView',
    fileDownload = 'fileDownload',
    dataQuery = 'dataQuery',
    dataExport = 'dataExport',
    pdfExport = 'pdfExport',
    userFeedback = 'userFeedback',
    aiChat = 'aiChat',
    aiFeedback = 'aiFeedback',
    companyUsage = 'companyUsage',
    conditionSearch = 'conditionSearch',
}

export type AnalyticsAPIParams = Record<string, string | undefined>;

export enum DeliveryMethod {
    download = 'download',
    email = 'email',
}

export enum TimeUnit {
    day = 'day',
    week = 'week',
    month = 'month',
    quarter = 'quarter',
    year = 'year',
}
